// table-features.ts — Markdown table editor: bare-`|` skeleton + smart paste.
//
// The pure, note-editor-FREE display/predict helpers (alignTableColumns,
// wrapTablesForScroll, _cleanupMarkdownTables, setupTableWheelScroll,
// predictCell) were extracted to the leaf editor/table-helpers.js so the
// editable table widget can import them without closing a cycle through
// note-editor.js. They are re-imported + re-exported below so this module's own
// callers (markdown-renderer.js, export-import.js) keep their
// paths unchanged.
//
// What stays HERE (because it needs NoteEditor / the editor's contentDOM):
//   Bare-`|` table-skeleton creation + the window.TablePredict override hook.
//   Smart paste: TSV/CSV → markdown table.
//
// Globals used: updateStatus

import { updateStatus } from '../ui/status-pill.js';
import { orderedInsertChange } from './list-renumber.js';
import { NoteEditor } from './note-editor.js';
import { refreshHighlight } from './preview-highlight.js';
import { isTableRowLine } from './table-source.js';
import {
  _cleanupMarkdownTables,
  alignTableColumns,
  predictCell,
  setupTableWheelScroll,
  wrapTablesForScroll,
} from './table-helpers.js';

// ── Table creation (bare-`|` skeleton) + predictor override hook ──────────
// The old whole-row "ghost autofill" preview was DELETED: it injected into the
// long-gone `_highlightPre` syntax-highlight surface so the ghost never
// rendered, yet its Enter-accept still silently inserted a predicted row below
// a table (a surprise edit with no preview). What survives here: the bare-`|`
// table-skeleton trick (which reuses `_getTableBodyRowsAbove` to avoid firing
// inside an existing table) and the `window.TablePredict` override hook (the
// per-cell placeholder autofill in the table widget reads it).
{
  function _parseCells(line: any) {
    const raw = line.trim();
    return raw
      .slice(1, raw.lastIndexOf('|'))
      .split('|')
      .map((c: any) => c.trim());
  }

  // Returns the body rows of the table immediately preceding lineIndex, or null.
  // Requires ≥ 2 body rows for pattern detection.
  function _getTableBodyRowsAbove(lines: any, lineIndex: any) {
    const rows = [];
    let i = lineIndex - 1;
    while (i >= 0 && isTableRowLine(lines[i])) {
      rows.unshift(lines[i]);
      i--;
    }
    if (rows.length < 3) return null; // need header + separator + ≥1 body
    // Find the separator line (cells = :?-+:? only)
    const sepIdx = rows.findIndex((row) => {
      const cells = _parseCells(row);
      return cells.length > 0 && cells.every((c: any) => /^:?-+:?$/.test(c));
    });
    // Require at least one header row above the separator — otherwise the
    // "table" has no header and the detected pipe-lines are something else
    // (e.g. a stray fragment or a body row that happens to look like a separator).
    if (sepIdx < 1) return null;
    const bodyRows = rows.slice(sepIdx + 1);
    return bodyRows.length >= 2 ? bodyRows : null;
  }

  // Bare `|` on an empty line → fresh table skeleton with focus in the first
  // header cell. Intercepted at keydown (not in onChange) so the user's `|`
  // never lands in the doc — one transaction creates the table, Ctrl-Z
  // removes it cleanly, and there's no one-frame flash of the raw pipe.
  //
  // Suppressed when the line above is already a markdown table with ≥ 2 body
  // rows, so it doesn't drop a fresh 2×2 skeleton when the user is plainly
  // trying to extend the table above (Tab off the last cell / Cmd-Enter add a
  // row to it instead).
  if (typeof NoteEditor.contentDOM === 'function')
    NoteEditor.contentDOM().addEventListener(
      'keydown',
      (e: any) => {
        if (e.key !== '|') return;
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (typeof NoteEditor.insertTableSkeleton !== 'function') return;
        if (document.querySelector('.cm-tooltip-autocomplete')) return;
        const sel = NoteEditor.getSelection();
        if (sel.anchor !== sel.head) return;
        const cursor = NoteEditor.getCursor();
        const lineText = NoteEditor.getLineText
          ? NoteEditor.getLineText(cursor.line + 1) || ''
          : '';
        if (lineText.trim() !== '') return; // empty/whitespace-only line only
        // Don't steal `|` from the autofill-row flow. Only the contiguous run
        // of table rows immediately above the cursor matters to
        // _getTableBodyRowsAbove, so walk up just that far rather than
        // materialising every line from 1..cursor.line (O(doc) per `|` keypress
        // near the bottom of a large note).
        const linesAbove = [];
        for (let i = cursor.line; i >= 1; i--) {
          const t = NoteEditor.getLineText(i) || '';
          if (!isTableRowLine(t)) break;
          linesAbove.unshift(t);
        }
        linesAbove.push('');
        if (_getTableBodyRowsAbove(linesAbove, linesAbove.length - 1)) return;
        e.preventDefault();
        e.stopPropagation();
        const lineStart = cursor.offset - cursor.ch;
        NoteEditor.insertTableSkeleton(lineStart, lineStart + lineText.length);
      },
      true
    );

  // Expose the per-column pattern predictor for the editable table widget as a
  // runtime override hook. The widget imports the same `predictCell` from the
  // leaf as its default, but prefers window.TablePredict.predictCell when set —
  // which is how the live-preview tests inject a stub predictor. Returning `''`
  // means "no clear pattern".
  window.TablePredict = { predictCell };
}

// Source-side keymap (Tab/Enter/Backspace) and active-row/col highlight
// for raw markdown tables previously lived here. Both were removed when
// table editing moved into the rendered widget (note-editor.js TableWidget):
// the widget is atomic, so the caret can never land on a table line — the
// old handlers became dead code. The widget owns navigation; the
// `:focus-within` rule in editor.css covers the visual focus indication.

// ── Smart paste: URL-wrap / list continuation / TSV·CSV·HTML → table ──────
// One conservative paste listener, deciding in this order (each branch only
// fires when it's confident; otherwise the browser default paste runs):
//   (a) a lone URL over a NON-EMPTY selection → wrap it as `[selection](url)`;
//   (b) multi-line text pasted INTO a list item → carry the list marker onto
//       each pasted line (ordered lists then renumber 1..N);
//   (c) tabular text (an HTML <table>, else TSV/CSV) on an EMPTY line → a
//       markdown table in place.
// Everything else pastes verbatim. TSV is preferred over CSV — a spreadsheet
// range always lands as TSV in `text/plain` — and a real HTML <table> is
// preferred over both (cleaner cell boundaries than tab/comma sniffing).
{
  function _parseTsv(text: any) {
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    if (lines.length && lines[lines.length - 1] === '') lines.pop();
    return lines.map((line: any) => line.split('\t'));
  }

  // Minimal RFC4180 CSV parser. Handles quoted cells, escaped quotes ("")
  // and quoted newlines within cells. Quotes are only treated specially when
  // they appear at the start of a cell.
  function _parseCsv(text: any) {
    const rows = [];
    let row = [];
    let cur = '';
    let inQ = false;
    let cellStart = true;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQ) {
        if (c === '"' && text[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (c === '"') inQ = false;
        else cur += c;
      } else if (c === '"' && cellStart) {
        inQ = true;
        cellStart = false;
      } else if (c === ',') {
        row.push(cur);
        cur = '';
        cellStart = true;
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(cur);
        rows.push(row);
        row = [];
        cur = '';
        cellStart = true;
      } else {
        cur += c;
        cellStart = false;
      }
    }
    if (cur !== '' || row.length) {
      row.push(cur);
      rows.push(row);
    }
    return rows;
  }

  // The single "what counts as a paste-able table" rule: ≥ 2 rows, ≥ 2 columns,
  // every row the same width. Shared by the TSV, CSV, and HTML paste paths.
  const _isTabularShape = (rows: any[]) =>
    rows.length >= 2 && rows[0].length >= 2 && rows.every((r: any) => r.length === rows[0].length);

  // Returns parsed rows if the text looks tabular (≥ 2 lines, ≥ 2 columns,
  // all rows the same width), else null.
  function _detectTabular(text: any) {
    if (!text?.includes('\n')) return null;
    const tsv = _parseTsv(text);
    if (_isTabularShape(tsv)) return tsv;
    if (!text.includes(',')) return null;
    const csv = _parseCsv(text);
    if (_isTabularShape(csv)) return csv;
    return null;
  }

  // Parse an HTML clipboard payload's first <table> into rows of trimmed cell
  // text, or null when there's no usable table. Preferred over the text/plain
  // TSV/CSV sniff because a spreadsheet's real `<td>`/`<th>` boundaries are
  // unambiguous (cells can contain tabs/commas that would fool _detectTabular).
  // Requires the same shape as _detectTabular (≥ 2 rows, ≥ 2 columns, uniform
  // width) so a stray one-cell layout table doesn't get converted.
  function _parseHtmlTable(html: any) {
    if (!html) return null;
    let doc: Document;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (_) {
      return null;
    }
    const table = doc.querySelector('table');
    if (!table) return null;
    const rows = [...table.querySelectorAll('tr')].map((tr) =>
      [...tr.querySelectorAll('th,td')].map((td) => (td.textContent || '').trim())
    );
    return _isTabularShape(rows) ? rows : null;
  }

  function _toMarkdownTable(rows: any) {
    const cols = rows[0].length;
    // Cells are escaped for embedded pipes and flattened to a single line —
    // markdown tables can't span newlines and a literal `|` would break the
    // column count.
    const fmtCell = (s: any) =>
      String(s).replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ').trim();
    const fmtRow = (r: any) => `| ${r.map(fmtCell).join(' | ')} |`;
    return [fmtRow(rows[0]), `|${' --- |'.repeat(cols)}`, ...rows.slice(1).map(fmtRow)].join('\n');
  }

  // A list item's leading `indent + marker (+ task) + separator`. Reused to
  // (1) detect that the caret sits in a list item and (2) skip pasted lines
  // that already carry their own marker (so we never produce `- - foo`).
  const LIST_PREFIX = /^(\s*)([-*+]|(\d+)\.)(\s+\[[ xX]\])?(\s+)/;
  // A lone URL over a selection → link. `\S+$` means a selection with internal
  // whitespace/newlines never matches, so only a single clean URL wraps.
  const URL_RE = /^(?:https?:\/\/|mailto:)\S+$/i;

  if (typeof NoteEditor.contentDOM === 'function')
    NoteEditor.contentDOM().addEventListener('paste', (e: any) => {
      // Pastes that originate inside a table-cell textarea must reach the
      // native textarea unmodified — the cell owns its own editing. Without
      // this guard, the doc-level conversions below see the bubbled event,
      // find the widget block's empty caret line, and rewrite the whole table.
      // MUST stay first so no smart-paste branch fires inside a cell.
      const tgt = e.target as Element | null;
      if (tgt && typeof tgt.closest === 'function' && tgt.closest('.cm-widget-table-input')) return;

      const plain = e.clipboardData?.getData('text/plain') || '';
      const html = e.clipboardData?.getData('text/html') || '';
      if (!plain && !html) return;

      const sel = NoteEditor.getSelection();
      const selFrom = Math.min(sel.anchor, sel.head);
      const selTo = Math.max(sel.anchor, sel.head);
      const hasSel = selFrom !== selTo;

      // (a) URL over a non-empty selection → `[selection](url)`. Only intercept
      // when the clipboard is a single clean URL AND the selection is PLAIN text
      // (#27). We must NOT wrap when the selection is itself a link or sits inside
      // one, or the paste produces a nested `[[link](url)](url)`. Three link-ish
      // cases fall through to the default paste (which replaces the selection):
      //   1. the selection already contains inline-link syntax (`](` — a full
      //      `[t](u)` / `![t](u)` selected);
      //   2. the selection is itself a bare URL;
      //   3. the selection sits inside a markdown-link span on its line.
      if (hasSel) {
        if (URL_RE.test(plain.trim())) {
          const content = NoteEditor.getContent();
          const selText = content.slice(selFrom, selTo);
          // Scan the selection's line for markdown-link spans; skip if one
          // fully contains the selection (the reported `[link](url)` repro).
          const lineStart = content.lastIndexOf('\n', selFrom - 1) + 1;
          const lineEndRaw = content.indexOf('\n', selTo);
          const lineEnd = lineEndRaw === -1 ? content.length : lineEndRaw;
          const lineText = content.slice(lineStart, lineEnd);
          const relFrom = selFrom - lineStart;
          const relTo = selTo - lineStart;
          const linkRe = /!?\[[^\]]*\]\([^)]*\)/g;
          let insideLink = false;
          let lm: RegExpExecArray | null;
          while ((lm = linkRe.exec(lineText)) !== null) {
            if (lm.index <= relFrom && relTo <= lm.index + lm[0].length) {
              insideLink = true;
              break;
            }
          }
          if (!insideLink && !/\]\(/.test(selText) && !URL_RE.test(selText.trim())) {
            e.preventDefault();
            const link = `[${selText}](${plain.trim()})`;
            NoteEditor.dispatchChange(
              { from: selFrom, to: selTo, insert: link },
              selFrom + link.length
            );
            refreshHighlight();
          }
        }
        return; // selection present but not a plain-text URL wrap → default paste
      }

      // From here on nothing is selected (caret only). Resolve the caret line.
      const pos = sel.anchor;
      const text = NoteEditor.getContent();
      const lineStart = text.lastIndexOf('\n', pos - 1) + 1;
      const lineEndRaw = text.indexOf('\n', pos);
      const lineEnd = lineEndRaw === -1 ? text.length : lineEndRaw;
      const lineText = text.slice(lineStart, lineEnd);

      // (c) TABLE — an HTML <table> (preferred) or TSV/CSV on an EMPTY line.
      // Pasting tabular text mid-paragraph is almost always meant literally, so
      // the empty-line gate stays; a non-empty line falls through to a default
      // paste rather than to the list branch (tabular content isn't a list).
      const rows = _parseHtmlTable(html) || _detectTabular(plain);
      if (rows) {
        if (lineText.trim() !== '') return;
        e.preventDefault();
        const md = _toMarkdownTable(rows);
        NoteEditor.dispatchChange(
          { from: lineStart, to: lineEnd, insert: md },
          lineStart + md.length
        );
        refreshHighlight();
        if (typeof updateStatus === 'function') {
          updateStatus('Pasted as markdown table.', true);
        }
        return;
      }

      // (b) LIST CONTINUATION — multi-line text pasted into a list item carries
      // that item's marker onto each pasted line, so the list keeps going
      // instead of collapsing into one paragraph. Ordered lists then renumber.
      if (plain.includes('\n')) {
        const m = LIST_PREFIX.exec(lineText);
        // Only continue an item that actually HAS body text — a bare `- ` /
        // `1. ` caret line isn't a list we should extend into.
        if (m && lineText.slice(m[0].length).trim() !== '') {
          const indent = m[1];
          const isOrdered = !!m[3];
          const sep = m[5] || ' ';
          const pasted = plain.replace(/\r\n?/g, '\n').split('\n');
          // The first pasted line merges onto the caret line as-is; each later
          // non-empty line that doesn't already carry a marker gets prefixed.
          // Ordered lines use a `1.` placeholder that the renumber pass rewrites
          // to the correct running number.
          const assembled = [pasted[0]];
          for (let i = 1; i < pasted.length; i++) {
            const ln = pasted[i];
            if (ln.trim() === '' || LIST_PREFIX.test(ln)) assembled.push(ln);
            else assembled.push(`${indent}${isOrdered ? '1.' : m[2]}${sep}${ln}`);
          }
          const insert = assembled.join('\n');
          e.preventDefault();
          // Splice + renumber as ONE change so a single Ctrl-Z reverts the whole
          // paste. orderedInsertChange no-ops the renumber for bullet lists.
          const anchorLineIdx = text.slice(0, pos).split('\n').length - 1;
          const chg = orderedInsertChange(text, insert, pos, anchorLineIdx);
          NoteEditor.dispatchChange(chg, pos + insert.length);
          refreshHighlight();
          return;
        }
      }
      // Nothing matched → default browser paste.
    });
}

// All former runtime window.* shims for the table helpers are gone:
// auto-edit-queue.js's 'table-cleanup' transform now imports
// _cleanupMarkdownTables statically from the note-editor-free leaf
// editor/table-helpers.js, and the display widget shims (alignTableColumns /
// wrapTablesForScroll) were already imported from that leaf by table-widget.js —
// none had remaining window readers.

// Re-export the leaf helpers so existing importers of table-features.js
// (markdown-renderer.js, export-import.js) keep their paths
// unchanged.
export { _cleanupMarkdownTables, alignTableColumns, setupTableWheelScroll, wrapTablesForScroll };
